import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignContentDto, EmailBodyDto, SegmentListDto } from '@carbonwebmail/contracts'
import { proseBody } from '@carbonwebmail/domain'
import { useCallback, useEffect, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'
import { usePreview } from '../../ui/usePreview.js'

/**
 * Writing a campaign, with the message beside it.
 *
 * Everything editable on a draft is here, not only the message: its name, who
 * it goes to, and the address it comes from. A composer that could fix a typo
 * in the body but not in the From address would send that typo to forty
 * thousand people.
 *
 * The preview comes from `usePreview`, which asks the server — see that hook
 * for why it is a request rather than a render.
 */

export type ComposerState = AsyncState<CampaignContentDto>

export interface Draft {
  readonly name: string
  /** Empty is the whole subscribed list, as on the create form. */
  readonly segmentId: string
  readonly fromName: string
  readonly fromEmail: string
  /** Empty means replies go to the From address. */
  readonly replyTo: string
  readonly subject: string
  /**
   * Prose or a design, tagged, because the two open different editors.
   *
   * A campaign written before the source was stored has neither, and that is
   * an empty prose body rather than a missing one — the screen says what it
   * cannot show and offers an editor rather than nothing.
   */
  readonly body: EmailBodyDto
}

export type SaveState =
  | { readonly status: 'idle' }
  | { readonly status: 'saving' }
  | { readonly status: 'saved' }
  /** The campaign was queued while the editor was open. */
  | { readonly status: 'not-a-draft' }
  | { readonly status: 'failed'; readonly detail: string }

export function useComposer({
  client,
  campaignId,
}: {
  readonly client: ApiClient
  readonly campaignId: string
}) {
  const load = useCallback(() => client.campaignContent(campaignId), [client, campaignId])
  const { state } = useLoaded<CampaignContentDto>(
    load,
    'We could not open that campaign. Please try again in a moment.',
  )

  const loadSegments = useCallback(() => client.segments(), [client])
  const { state: segments } = useLoaded<SegmentListDto>(
    loadSegments,
    'We could not load your segments, so the only audience available is your whole list.',
  )

  const [draft, setDraft] = useState<Draft | undefined>(undefined)
  const [save, setSave] = useState<SaveState>({ status: 'idle' })

  /**
   * The editor is filled once, when the campaign arrives.
   *
   * Guarded on the draft rather than on the loaded value, so nothing can
   * overwrite what somebody has typed since.
   */
  useEffect(() => {
    if (state.status !== 'ready' || draft !== undefined) return
    setDraft({
      name: state.value.name,
      segmentId: state.value.segmentId ?? '',
      fromName: state.value.fromName,
      fromEmail: state.value.fromEmail,
      replyTo: state.value.replyTo,
      subject: state.value.subject,
      body: state.value.body ?? proseBody(''),
    })
  }, [state, draft])

  const preview = usePreview({
    client,
    subject: draft?.subject ?? '',
    body: draft?.body ?? proseBody(''),
  })

  const change = useCallback(<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => (current === undefined ? current : { ...current, [key]: value }))
    setSave({ status: 'idle' })
  }, [])

  const commit = useCallback(async () => {
    if (draft === undefined) return
    setSave({ status: 'saving' })
    try {
      const outcome = await client.rewriteCampaign(campaignId, {
        name: draft.name,
        // Absent rather than empty: the contract's "everyone" is a missing
        // segment, and an empty string is not a uuid.
        segmentId: draft.segmentId === '' ? null : draft.segmentId,
        fromName: draft.fromName,
        fromEmail: draft.fromEmail,
        replyTo: draft.replyTo,
        subject: draft.subject,
        body: draft.body,
      })
      setSave(outcome.status === 'rewritten' ? { status: 'saved' } : { status: 'not-a-draft' })
    } catch {
      setSave({
        status: 'failed',
        detail: 'We could not save that. Please check the fields and try again.',
      })
    }
  }, [client, campaignId, draft])

  return { state, segments, draft, preview, save, change, commit }
}
